import { isEmail, isIP, isUrl, isUUIDv7, isValidPhoneNumber } from '@faceup/utils'
import * as yup from 'yup'

/**
 * Makes custom validation methods work with nullable fields by allowing undefined/null values.
 * This enables chains like yup.string().email().nullable() to work correctly.
 *
 * @see https://github.com/jquense/yup#validation-tests
 */
const validateNullableValue = <T>(
  value: T | undefined | null,
  validateDefinedValue: (value: T) => boolean
): boolean => {
  if (value === undefined || value === null) {
    return true // Allow empty values
  }

  return validateDefinedValue(value)
}

export type CustomYupValidators = 'email' | 'url' | 'ipAddress' | 'phone' | 'file' | 'uuid'

export type CustomYupValidatorsErrorMessages = {
  [K in CustomYupValidators]: string
}

let errorMessages: CustomYupValidatorsErrorMessages = {
  email: 'Invalid email address',
  url: 'Invalid URL format',
  ipAddress: 'Invalid IP address format',
  phone: 'Invalid phone number format',
  file: 'Invalid file format',
  uuid: 'Invalid UUID format',
}

const getErrorMessage = (key: CustomYupValidators): string => {
  return errorMessages[key]
}

/**
 * Set error messages for the custom validators. This makes it possible to localize the messages.
 */
export const setCustomYupValidatorsErrorMessages = (messages: CustomYupValidatorsErrorMessages) => {
  errorMessages = messages
}

export const registerCustomYupValidators = () => {
  yup.addMethod(yup.string, 'email', function (message?: string) {
    return this.test('email', message ?? getErrorMessage('email'), value =>
      validateNullableValue(value, isEmail)
    )
  })

  yup.addMethod(yup.string, 'url', function (message?: string) {
    return this.test('url', message ?? getErrorMessage('url'), value =>
      validateNullableValue(value, isUrl)
    )
  })

  yup.addMethod(yup.string, 'ipAddress', function (message?: string) {
    return this.test('ipAddress', message ?? getErrorMessage('ipAddress'), value =>
      validateNullableValue(value, isIP)
    )
  })

  yup.addMethod(yup.string, 'phone', function (message?: string) {
    return this.test('phone', message ?? getErrorMessage('phone'), value =>
      validateNullableValue(value, isValidPhoneNumber)
    )
  })

  yup.addMethod(yup.string, 'uuid', function (message?: string) {
    return this.test('uuid', message ?? getErrorMessage('uuid'), value =>
      validateNullableValue(value, isUUIDv7)
    )
  })

  yup.addMethod(yup.mixed, 'file', function (message?: string) {
    return this.test('file', message ?? getErrorMessage('file'), value =>
      validateNullableValue<unknown>(value, val => val instanceof File)
    )
  })
}

registerCustomYupValidators()

declare module 'yup' {
  interface StringSchema {
    email(message?: string): this
    url(message?: string): this
    ipAddress(message?: string): this
    phone(message?: string): this
  }
  interface MixedSchema {
    file(message?: string): this
  }
}
